import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { Button } from "../Button";
import { SelectDropDown } from "../SelectDropDown";
import styles from "./OverlaySelector.scss";

const propTypes = {
  /**
   * Options of the select field
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
    })
  ),
  /**
   * Selected value
   */
  value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to remove the selected option
   */
  onRemove: PropTypes.func.isRequired,
  /**
   * Callback to save the selected option
   */
  onSave: PropTypes.func.isRequired,
  /**
   * Message that appears when nothing is selected
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Class to style the button (anchor) of the tooltip
   */
  buttonClassName: PropTypes.string,
  /**
   * Additional props to pass to the Select component
   */
  selectProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Id of the button that opens the tooltip
   */
  buttonId: PropTypes.string,
  /**
   * If true, button is disabled
   */
  disabled: PropTypes.bool,
};

const defaultProps = {
  options: {},
  value: "",
  buttonClassName: "",
  selectProps: {},
  buttonId: undefined,
  disabled: false,
};

/**
 * OverlaySelector works like a single Select field but with a tooltip menu that opens when cliked.
 * When a value is selected the only option is to remove it.
 * When a value is not selected it displays a select field and a save button
 */
function OverlaySelector(props) {
  const {
    options,
    value,
    onRemove,
    onSave,
    placeholder,
    buttonClassName,
    selectProps,
    buttonId,
    disabled,
  } = props;

  const selectedLabel = options?.find((item) => item.value === value)?.label;

  const [show, setShow] = useState(false);

  const hide = useCallback(() => setShow(false), []);

  const overlay = selectedLabel ? (
    <SelectedOverlay
      label={selectedLabel}
      onRemove={onRemove}
      onCancel={hide}
    />
  ) : (
    <UnselectedOverlay
      options={options}
      onSave={onSave}
      onCancel={hide}
      placeholder={placeholder}
      selectProps={selectProps}
    />
  );

  return (
    <OverlayTrigger
      trigger="click"
      show={show}
      rootClose
      onToggle={setShow}
      onHide={hide}
      placement="bottom"
      overlay={<Popover className={styles.popover}>{overlay}</Popover>}
    >
      {selectedLabel ? (
        <Button
          id={buttonId}
          variant="dashed"
          className={buttonClassName}
          disabled={disabled}
        >
          {selectedLabel}
        </Button>
      ) : (
        <Button
          id={buttonId}
          variant="link"
          className={buttonClassName}
          disabled={disabled}
        >
          {placeholder}
        </Button>
      )}
    </OverlayTrigger>
  );
}

OverlaySelector.propTypes = propTypes;
OverlaySelector.defaultProps = defaultProps;

export default OverlaySelector;

/* Support Components */
// eslint-disable-next-line react/prop-types
function SelectedOverlay({ label, onRemove, onCancel }) {
  return (
    <>
      <Popover.Title as="h3" className={styles.popoverHeader}>
        <div className={styles.selectedLabel}>
          <FormattedMessage id="UIC.GENERAL.SELECTED" />:
        </div>
        <div>{label}</div>
      </Popover.Title>
      <Popover.Content className="d-flex justify-content-between">
        <Button variant="light" onClick={onCancel}>
          <FormattedMessage id="UIC.GENERAL.CANCEL" />
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            onRemove();
            onCancel();
          }}
        >
          <FormattedMessage id="UIC.GENERAL.REMOVE" />
        </Button>
      </Popover.Content>
    </>
  );
}

/* eslint-disable react/prop-types */
function UnselectedOverlay({
  options,
  onSave,
  onCancel,
  placeholder,
  selectProps,
}) {
  const [selected, setSelected] = useState(null);

  return (
    <>
      <Popover.Title as="h3" className={styles.popoverHeader}>
        <SelectDropDown
          {...selectProps}
          options={options}
          value={selected}
          isClearable
          onChange={setSelected}
          placeholder={placeholder}
        />
      </Popover.Title>
      <Popover.Content className="d-flex justify-content-between">
        <Button variant="light" onClick={onCancel}>
          <FormattedMessage id="UIC.GENERAL.CANCEL" />
        </Button>
        <Button
          disabled={!selected?.value}
          onClick={() => {
            onSave(selected);
            onCancel();
          }}
        >
          <FormattedMessage id="UIC.GENERAL.SAVE" />
        </Button>
      </Popover.Content>
    </>
  );
}
/* eslint-enable react/prop-types */
